/**
 * Error handling utilities for CCMS components
 */

/**
 * Extract error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }
    if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }
  }

  return 'An unknown error occurred';
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.message.includes('network') ||
      error.message.includes('fetch') ||
      error.message.includes('NetworkError') ||
      error.name === 'NetworkError'
    );
  }
  return false;
}

/**
 * Check if error is a timeout error
 */
export function isTimeoutError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.message.includes('timeout') ||
      error.message.includes('timed out') ||
      error.name === 'TimeoutError'
    );
  }
  return false;
}

/**
 * Check if error is an authorization error
 */
export function isAuthError(error: unknown): boolean {
  if (error && typeof error === 'object') {
    if ('statusCode' in error) {
      return error.statusCode === 401 || error.statusCode === 403;
    }
  }
  return false;
}

/**
 * Format error for user display
 */
export function formatErrorForUser(error: unknown): string {
  if (isNetworkError(error)) {
    return 'Unable to connect. Please check your internet connection.';
  }

  if (isTimeoutError(error)) {
    return 'Request timed out. Please try again.';
  }

  if (isAuthError(error)) {
    return 'You do not have permission to perform this action.';
  }

  const message = getErrorMessage(error);

  // Don't show technical error messages to users
  if (message.includes('500') || message.includes('Internal Server Error')) {
    return 'Something went wrong. Please try again later.';
  }

  return message;
}

/**
 * Create a user-friendly error object
 */
export interface UserFriendlyError {
  title: string;
  message: string;
  canRetry: boolean;
  originalError?: unknown;
}

export function createUserFriendlyError(error: unknown): UserFriendlyError {
  if (isNetworkError(error)) {
    return {
      title: 'Connection Error',
      message: 'Unable to connect. Please check your internet connection.',
      canRetry: true,
      originalError: error,
    };
  }

  if (isTimeoutError(error)) {
    return {
      title: 'Request Timeout',
      message: 'The request took too long. Please try again.',
      canRetry: true,
      originalError: error,
    };
  }

  if (isAuthError(error)) {
    return {
      title: 'Permission Denied',
      message: 'You do not have permission to perform this action.',
      canRetry: false,
      originalError: error,
    };
  }

  return {
    title: 'Error',
    message: formatErrorForUser(error),
    canRetry: true,
    originalError: error,
  };
}
